import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, firstValueFrom, of } from 'rxjs';
import AuthApiService from '@shared/services/auth/auth-api.service';
import { AuthService } from '@modules/components/auth/auth.service';
import { AccountSwitchReason } from '@shared/services/account-switch/account-switch.enums';
import { AccountSwitchPending } from '@shared/services/account-switch/account-switch.types';
import {
  buildAccountSwitchUrl,
  isSessionAlive,
  readSessionAccount,
} from '@shared/services/account-switch/account-switch.util';
import { LOGIN_LINK_FRAGMENT_PARAM } from './login-link.constants';

@Injectable({ providedIn: 'root' })
export class LoginLinkService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly authApiService = inject(AuthApiService);
  private readonly authService = inject(AuthService);

  public readonly pending = signal<AccountSwitchPending | null>(null);

  public async init(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const token = fragment.get(LOGIN_LINK_FRAGMENT_PARAM);

    if (!token) {
      return;
    }

    const next = window.location.pathname + window.location.search;

    window.history.replaceState(window.history.state, '', next);

    const session = isSessionAlive(this.authService.refreshToken)
      ? readSessionAccount(this.authService.jwtToken)
      : null;

    if (!session) {
      await this.signIn(token);
      return;
    }

    const owner = await firstValueFrom(
      this.authApiService
        .previewLoginLink(token)
        .pipe(catchError(() => of(null))),
    );

    if (!owner) {
      return;
    }

    if (owner.userId === session.id) {
      await this.signIn(token);
      return;
    }

    this.pending.set({ token, owner, next });
    window.history.replaceState(
      window.history.state,
      '',
      buildAccountSwitchUrl(AccountSwitchReason.LINK, next),
    );
  }

  public clearPending(): void {
    this.pending.set(null);
  }

  private async signIn(token: string): Promise<void> {
    const response = await firstValueFrom(
      this.authApiService.loginWithLink(token).pipe(catchError(() => of(null))),
    );

    if (!response) {
      return;
    }

    if (
      this.authService.jwtToken &&
      readSessionAccount(this.authService.jwtToken)?.id !== response.user.id
    ) {
      this.authService.removeToken();
    }

    this.authService.setLoggedInData(
      response.access_token,
      response.refresh_token,
      response.user,
    );
  }
}
